import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Eye, MessageCircle, Phone, Heart, Share2, Calendar, Users, Home, TrendingUp, Building2 } from 'lucide-react';

const ICON_MAP = {
  profile_views: Eye, agency_views: Building2, property_views: Home,
  whatsapp_clicks: MessageCircle, phone_clicks: Phone,
  favorites: Heart, shares: Share2, contacts: MessageCircle,
  visit_requests: Calendar, leads: Users, active_properties: Home,
  active_members: Users,
};

const LABEL_MAP = {
  profile_views: 'Vistas perfil',
  agency_views: 'Vistas inmobiliaria',
  property_views: 'Vistas propiedades',
  whatsapp_clicks: 'Clics WhatsApp',
  phone_clicks: 'Clics teléfono',
  favorites: 'Favoritos',
  shares: 'Compartidos',
  contacts: 'Contactos',
  visit_requests: 'Visitas agendadas',
  leads: 'Leads',
  active_properties: 'Propiedades activas',
  active_members: 'Asesores activos',
};

const SERIES_LABELS = {
  'agent.profile_viewed': 'Perfil',
  'agency.profile_viewed': 'Inmobiliaria',
  'property.viewed': 'Propiedades',
  'contact.whatsapp_clicked': 'WhatsApp',
  'contact.phone_clicked': 'Teléfono',
};

const SERIES_COLORS = ['#0d9488', '#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6'];

export function KpiGrid({ kpis = {}, order, testid = 'kpi-grid' }) {
  const keys = order || Object.keys(kpis);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={testid}>
      {keys.map(k => {
        const Icon = ICON_MAP[k] || TrendingUp;
        return (
          <div key={k} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`kpi-${k}`}>
            <div className="flex items-center gap-2 text-gray-500">
              <Icon className="w-4 h-4 text-habita" />
              <p className="text-[10px] uppercase tracking-wider">{LABEL_MAP[k] || k}</p>
            </div>
            <p className="font-serif text-2xl font-bold text-habita-dark mt-2 tabular-nums" data-testid={`kpi-${k}-value`}>{(kpis[k] ?? 0).toLocaleString('es-CO')}</p>
          </div>
        );
      })}
    </div>
  );
}

export function SeriesChart({ series = [], testid = 'series-chart' }) {
  if (!series.length) return null;
  // Determine which keys to chart (exclude `date`)
  const sample = series[0] || {};
  const eventKeys = Object.keys(sample).filter(k => k !== 'date');

  // Format dates for x-axis (only show day-of-month + abbrev month)
  const data = series.map(row => ({
    ...row,
    _label: row.date.length >= 10
      ? new Date(row.date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
      : row.date,
  }));

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
      <p className="font-serif text-base font-bold text-habita-dark mb-3">Evolución</p>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              {eventKeys.map((k, i) => (
                <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={SERIES_COLORS[i % SERIES_COLORS.length]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#f3f4f6" />
            <XAxis dataKey="_label" tick={{ fontSize: 11, fill: '#6b7280' }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(v, k) => [v, SERIES_LABELS[k] || k]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(k) => SERIES_LABELS[k] || k} />
            {eventKeys.map((k, i) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                fill={`url(#grad-${k})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
